import React, { useEffect, useState } from 'react';
import { CompanyMetrics, UserSession } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { api } from '../services/api';
import { BatchInviteModal } from '../components/BatchInviteModal';
import { Users, UserCheck, Clock, Download, UserPlus, CheckCircle, Shield } from 'lucide-react';

interface B2BDashboardPageProps {
  session: UserSession;
}

export const B2BDashboardPage: React.FC<B2BDashboardPageProps> = ({ session }) => {
  const { t } = useLanguage();
  const [metrics, setMetrics] = useState<CompanyMetrics | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const fetchMetrics = () => {
    if (session.companyId) {
      api.getCompanyMetrics(session.companyId).then(setMetrics);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, [session.companyId]);

  const handleExportCsv = () => {
    alert(t('Descargando Reporte de Cumplimiento CSV...', 'Downloading Compliance Report CSV...'));
  };

  if (!metrics) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading metrics...</div>;
  }

  return (
    <div style={{ padding: '32px 24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header & Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <span className="badge-status in-progress">{t('Panel de Administración B2B', 'B2B Enterprise Admin')}</span>
          <h1 style={{ fontSize: '28px', fontWeight: 800, marginTop: '4px' }}>{metrics.companyName}</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            {t('Monitoreo de cumplimiento de capacitaciones anuales y auditoría.', 'Annual compliance training metrics & audit readiness.')}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-secondary" onClick={handleExportCsv}>
            <Download size={16} />
            {t('Exportar Reporte CSV', 'Export CSV Report')}
          </button>
          <button className="btn btn-primary" onClick={() => setShowInviteModal(true)}>
            <UserPlus size={16} />
            {t('Invitar Empleados (CSV)', 'Batch Invite Employees')}
          </button>
        </div>
      </div>

      {/* Top Metric Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '36px' }}>
        <div className="glass-card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>{t('Total Empleados', 'Total Employees')}</span>
            <Users size={18} color="var(--accent-primary)" />
          </div>
          <div style={{ fontSize: '32px', fontWeight: 800, marginTop: '8px' }}>{metrics.totalEmployees}</div>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{metrics.activeLicenses} {t('licencias activas', 'active licenses')}</span>
        </div>

        <div className="glass-card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>{t('Cumplimiento General', 'Overall Compliance')}</span>
            <Shield size={18} color="var(--accent-success)" />
          </div>
          <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--accent-success)', marginTop: '8px' }}>
            {metrics.overallCompliancePercentage}%
          </div>
          <span style={{ fontSize: '12px', color: 'var(--accent-success)' }}>● {t('Auditoría Aprobada', 'Audit Compliant')}</span>
        </div>

        <div className="glass-card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>{t('Certificados Completados', 'Completed Certs')}</span>
            <CheckCircle size={18} color="var(--accent-success)" />
          </div>
          <div style={{ fontSize: '32px', fontWeight: 800, marginTop: '8px' }}>{metrics.enrollmentBreakdown.completed}</div>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{metrics.enrollmentBreakdown.inProgress} {t('en curso', 'in progress')}</span>
        </div>

        <div className="glass-card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>{t('Pendientes por Iniciar', 'Not Started')}</span>
            <Clock size={18} color="var(--accent-warning)" />
          </div>
          <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--accent-warning)', marginTop: '8px' }}>
            {metrics.enrollmentBreakdown.notStarted}
          </div>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t('Recordatorio enviado', 'Reminder dispatched')}</span>
        </div>
      </div>

      {/* Course Breakdown Table */}
      <div className="glass-card" style={{ padding: '24px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px' }}>
          {t('Desglose de Cumplimiento por Curso Obligatorio', 'Mandatory Course Compliance Breakdown')}
        </h3>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
                <th style={{ padding: '12px' }}>{t('Curso Obligatorio', 'Mandatory Course')}</th>
                <th style={{ padding: '12px' }}>{t('Inscritos', 'Enrolled')}</th>
                <th style={{ padding: '12px' }}>{t('Completados', 'Completed')}</th>
                <th style={{ padding: '12px' }}>{t('Cumplimiento %', 'Compliance %')}</th>
                <th style={{ padding: '12px' }}>{t('Estado', 'Status')}</th>
              </tr>
            </thead>
            <tbody>
              {metrics.courseMetrics.map((c) => (
                <tr key={c.courseId} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '16px 12px', fontWeight: 600 }}>{c.courseId}</td>
                  <td style={{ padding: '16px 12px' }}>{c.totalEnrolled}</td>
                  <td style={{ padding: '16px 12px' }}>{c.completedCount}</td>
                  <td style={{ padding: '16px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ flex: 1, height: '8px', background: 'var(--bg-secondary)', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${c.compliancePercent}%`, height: '100%', background: 'var(--gradient-badge)' }} />
                      </div>
                      <span style={{ fontWeight: 700, minWidth: '40px' }}>{c.compliancePercent}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '16px 12px' }}>
                    <span className="badge-status completed">
                      <UserCheck size={12} /> {t('Al Día', 'Compliant')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showInviteModal && session.companyId && (
        <BatchInviteModal
          companyId={session.companyId}
          onClose={() => setShowInviteModal(false)}
          onInvitesSent={() => {
            fetchMetrics();
          }}
        />
      )}
    </div>
  );
};
